import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { useCompaniesList } from '@/features/company';
import { Box, MenuItem, Notification } from '@/shared/ui';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { DialogAlert } from '@/shared/ui/DialogAlert';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';
import { ControlledSelect } from '@/shared/ui/Select';

import { selectCompanySchemaFormData } from '../../model/schemas';
import { TModeCompanyDialog, TSelectCompanyFormData } from '../../model/types';

export interface ISelectCompanyDialogProps
  extends Omit<TDialogBase, 'onSubmit'> {
  onSubmit: (data: TSelectCompanyFormData) => void;
  isPending?: boolean;
  currentCompanyId?: string;
  dialogTitle?: string;
  leftButtonText?: string;
  rightButtonText?: string;
  mode?: TModeCompanyDialog;
}

export const SelectCompanyDialog = ({
  open,
  onClose,
  onSubmit,
  isPending,
  currentCompanyId,
  mode = 'add',
  ...otherProps
}: ISelectCompanyDialogProps) => {
  const { t } = useTranslation();

  const {
    companies,
    isPending: isPendingCompaniesList,
    isError: isErrorCompaniesList,
    error: errorCompaniesList,
  } = useCompaniesList(open);

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(selectCompanySchemaFormData),
    mode: 'onChange',
    values: {
      companyId: currentCompanyId || '',
    },
  });

  const handleClose = () => {
    onClose?.();
    reset();
  };

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title={
          mode === 'transferOrRemove'
            ? t('channels:transferOrRemoveChannelTitle')
            : t('channels:addChannelTitle')
        }
        leftButtonSlot={
          <Button variant="outlined" onClick={handleClose}>
            {t('common:buttons.cancel')}
          </Button>
        }
        rightButtonSlot={
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            loading={isPending}
          >
            {mode === 'transferOrRemove'
              ? t('channels:buttons.confirmTransferOrRemove')
              : t('channels:buttons.addChannel')}
          </Button>
        }
        {...otherProps}
      >
        <Box component="form" sx={{ width: '500px' }}>
          {!companies && isPendingCompaniesList ? (
            <Loader containerSize="extraSmall" />
          ) : (
            <>
              <DialogAlert sx={{ mb: '25px' }}>
                {t('company:addCompanyInfo')}
              </DialogAlert>
              <ControlledSelect
                control={control}
                name="companyId"
                variant="outlined"
                label={t('company:inputs.selectCompanyLabel')}
                displayEmpty
              >
                <MenuItem key="default" value="">
                  {t('company:inputs.selectNotCompany')}
                </MenuItem>
                {companies?.map((company) => (
                  <MenuItem
                    key={company.id}
                    value={company.id}
                    title={company.name}
                  >
                    {company.name}
                  </MenuItem>
                ))}
              </ControlledSelect>
            </>
          )}
        </Box>
      </Dialog>
      <Notification
        open={isErrorCompaniesList}
        title={errorCompaniesList?.message}
      >
        {errorCompaniesList?.data?.details}
      </Notification>
    </>
  );
};
